import React from 'react';
import { getIn, useFormikContext } from 'formik';

import {
  getReactionsPropertyIndex,
  mapReactionsWithChecks,
  shouldCheckReactionsProperty,
} from '../PrototypeProperties/PrototypeProperties.helpers';

import PrototypeSection from './PrototypeSection';

const PrototypeSectionContainer = ({
  properties,
  namePrefix,
  isInitialState,
  taskIndex,
  ...restProps
}: {
  properties: any;
  namePrefix: any;
  isInitialState: boolean;
  taskIndex: number | 'INITIAL';
} & Record<string, any>) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const taskStatePrefix =
    isInitialState || taskIndex === 'INITIAL'
      ? 'initialState'
      : `tasks[${taskIndex}]`;
  const taskState = getIn(values, taskStatePrefix) || {};
  const flowStartingPoints = taskState.flowStartingPoints || [];
  const prototypeStartNode = taskState.prototypeStartNode || null;

  const getReactionsPath = () => {
    const reactionsPropertyIndex = getReactionsPropertyIndex(properties);

    if (reactionsPropertyIndex === -1) {
      return null;
    }

    return `${namePrefix}.properties[${reactionsPropertyIndex}]`;
  };

  const handleInteractionsCategoryCheckChanged = (checked: boolean) => {
    const reactionsPath = getReactionsPath();

    if (!reactionsPath) {
      return;
    }

    const reactionsProperty = getIn(values, reactionsPath);
    const reactions = Array.isArray(reactionsProperty?.value)
      ? reactionsProperty.value
      : [];

    setFieldValue(
      `${reactionsPath}.value`,
      mapReactionsWithChecks({ reactions, checked }),
    );
    setFieldValue(`${reactionsPath}.check`, checked);
  };

  const handleReactionPropertyChanged = (name: string, checked: boolean) => {
    const reactionsPath = getReactionsPath();

    if (!reactionsPath) {
      return;
    }

    const match = name.match(/\.value\[(\d+)\]\.checks\.(.+)$/);

    if (!match) {
      return;
    }

    const reactionsProperty = getIn(values, reactionsPath);
    const reactions = Array.isArray(reactionsProperty?.value)
      ? reactionsProperty.value
      : [];
    const interactionIndex = Number(match[1]);
    const propertyKey = match[2];

    const updatedReactions = reactions.map((interaction: Record<string, unknown>, index: number) => {
      if (index !== interactionIndex) {
        return interaction;
      }

      return {
        ...interaction,
        checks: {
          ...((interaction.checks as Record<string, boolean>) ?? {}),
          [propertyKey]: checked,
        },
      };
    });

    setFieldValue(
      `${reactionsPath}.check`,
      shouldCheckReactionsProperty(updatedReactions),
    );
  };

  const handleFlowCategoryCheckChanged = (checked: boolean) => {
    flowStartingPoints.forEach((_: unknown, index: number) => {
      setFieldValue(
        `${taskStatePrefix}.flowStartingPoints[${index}].checks.name`,
        checked,
      );
      setFieldValue(
        `${taskStatePrefix}.flowStartingPoints[${index}].checks.nodeName`,
        checked,
      );
    });
  };

  const handleStartNodeCategoryCheckChanged = (checked: boolean) => {
    if (!prototypeStartNode) {
      return;
    }

    setFieldValue(
      `${taskStatePrefix}.prototypeStartNode.checks.name`,
      checked,
    );
    setFieldValue(
      `${taskStatePrefix}.prototypeStartNode.checks.type`,
      checked,
    );
  };

  return (
    <PrototypeSection
      {...restProps}
      isInitialState={isInitialState}
      designType={values.designType}
      taskStatePrefix={taskStatePrefix}
      flowStartingPoints={flowStartingPoints}
      prototypeStartNode={prototypeStartNode}
      properties={properties}
      namePrefix={namePrefix}
      onInteractionsCategoryCheckChanged={handleInteractionsCategoryCheckChanged}
      onReactionPropertyChanged={handleReactionPropertyChanged}
      onFlowCategoryCheckChanged={handleFlowCategoryCheckChanged}
      onStartNodeCategoryCheckChanged={handleStartNodeCategoryCheckChanged}
    />
  );
};

export default React.memo(PrototypeSectionContainer) as any;
